import React, { useState } from 'react';
import { Animated, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import RelicSprite from '../shared/RelicSprite';
import ShimmerHalo from '../shared/ShimmerHalo';
import { CheckIcon, CloseIcon, CoinIcon, LockIcon } from '../shared/RelicVaultIcons';
import { FLY_EASING, useOneShot } from '../shared/useRelicVaultAnimations';
import { useRelicVaultLayout } from '../shared/useRelicVaultLayout';
import {
  COLORS,
  bodyFont,
  em,
  headingFont,
  modalBackdrop,
  modalSurface,
} from '../shared/theme';
import { iconGlowFor } from 'utils/relicVault/colorUtils';
import {
  detailDescriptionFor,
  guildLabelFor,
  rarityColorFor,
  undiscoveredName,
} from 'utils/relicVault/catalog';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

const CARD_WIDTH = 380;
const ART_SIZE = 240;
const PHOTO_SIZE = 210; // 160px icon at the design's 131.25%

/** Relic detail popup in the vault, with the sell-back flow for owned relics. */
const RelicDetailModal = ({ catalog, relic, browser }) => {
  const { width, isPhone } = useRelicVaultLayout();
  if (!relic) return null;

  const confirming = !!browser.sellConfirmId;
  // The refund is half the tier price, the same rule the server applies.
  const tier = catalog.tierByKey.get(relic.rarity);
  const rarityColor = tier?.colorHex || '#8B5CF6';
  const refund = Math.floor((tier?.relicPrice || 0) * 0.5);
  const cardWidth = isPhone ? width * 0.92 : Math.min(CARD_WIDTH, width - 48);

  return (
    <View style={[StyleSheet.absoluteFill, styles.backdrop, modalBackdrop]}>
      <TouchableOpacity
        activeOpacity={1}
        style={StyleSheet.absoluteFill}
        onPress={browser.closeDetail}
        accessibilityLabel="Close"
      />
      <View style={[styles.card, modalSurface, { width: cardWidth }, isPhone && styles.cardPhone]}>
        <TouchableOpacity
          onPress={browser.closeDetail}
          accessibilityRole="button"
          accessibilityLabel="Close"
          style={styles.closeBtn}>
          <CloseIcon />
        </TouchableOpacity>

        <View
          style={[
            styles.art,
            relic.owned
              ? IS_WEB
                ? { backgroundImage: iconGlowFor(rarityColor) }
                : { backgroundColor: `${rarityColor}22` }
              : { backgroundColor: 'rgba(255,255,255,0.04)' },
          ]}>
          <RelicSprite relic={relic} grid={catalog.sheetGrid} size={PHOTO_SIZE} locked={!relic.owned} />
          {!relic.owned && (
            <View style={styles.lockOverlay}>
              <LockIcon size={24} strokeWidth="2" opacity={0.85} />
            </View>
          )}
          <ShimmerHalo
            glow={relic.owned ? `${rarityColor}80` : 'rgba(255,255,255,0.25)'}
            radius={18}
            haloSize={30}
            haloSpread={5}
            replayKey={relic.relicId}
            delay={100}
          />
        </View>

        <View style={[styles.badge, { borderColor: rarityColor }]}>
          <Text style={[styles.badgeText, { color: rarityColor }]}>{tier?.label}</Text>
        </View>

        {confirming ? (
          <SellView relic={relic} refund={refund} browser={browser} />
        ) : (
          <>
            <Text style={styles.name}>{relic.owned ? relic.name : undiscoveredName(catalog, relic)}</Text>
            <Text style={styles.guild}>{guildLabelFor(catalog, relic)}</Text>
            <Text style={styles.desc}>{detailDescriptionFor(catalog, relic)}</Text>
            {relic.owned ? (
              <View style={styles.statusRow}>
                <View style={[styles.status, styles.statusOwned]}>
                  <CheckIcon size={12} />
                  <Text style={[styles.statusText, styles.statusTextOwned]}>In Your Collection</Text>
                </View>
                <SellButton onPress={() => browser.startSell(relic.relicId)} />
              </View>
            ) : (
              <View style={[styles.status, styles.statusLocked, styles.statusSolo]}>
                <Text style={[styles.statusText, { color: COLORS.muted }]}>Not Yet Discovered</Text>
              </View>
            )}
          </>
        )}
      </View>
    </View>
  );
};

const SellButton = ({ onPress }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={onPress}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      style={[styles.sellBtn, hovered && styles.sellBtnHover]}>
      <Text style={styles.sellBtnText}>Sell</Text>
    </TouchableOpacity>
  );
};

const SellView = ({ relic, refund, browser }) => {
  const fly = useOneShot(browser.sellAnimating, 650, FLY_EASING);
  const stops = [0, 0.6, 1];

  return (
    <View style={styles.sellView}>
      <Text style={styles.sellPrompt}>
        Sell <Text style={styles.sellName}>{relic.name}</Text> for{' '}
        <Text style={styles.sellCoin}>{refund} QuikCoins</Text>?
      </Text>
      {browser.sellAnimating ? (
        <View style={styles.coinFly}>
          <Animated.View
            style={{
              opacity: fly.interpolate({ inputRange: stops, outputRange: [1, 1, 0] }),
              transform: [
                { translateY: fly.interpolate({ inputRange: stops, outputRange: [0, -70, -140] }) },
                { scale: fly.interpolate({ inputRange: stops, outputRange: [1, 0.85, 0.5] }) },
              ],
            }}>
            <CoinIcon size={30} />
          </Animated.View>
        </View>
      ) : (
        <View style={styles.sellActions}>
          <TouchableOpacity
            activeOpacity={0.9}
            onPress={browser.cancelSell}
            style={[styles.sellActionBtn, styles.sellCancel]}>
            <Text style={[styles.sellActionText, { color: COLORS.lilacSoft }]}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.9}
            onPress={() => browser.confirmSell(relic.relicId)}
            style={[
              styles.sellActionBtn,
              styles.sellConfirm,
              webOnly({ backgroundImage: 'linear-gradient(135deg, #F0A93A, #C97F13)' }),
            ]}>
            <Text style={[styles.sellActionText, styles.sellConfirmText]}>Confirm Sale</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    zIndex: 1000,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.14)',
    borderRadius: 24,
    paddingTop: 32,
    paddingHorizontal: 28,
    paddingBottom: 28,
    alignItems: 'center',
  },
  cardPhone: {
    paddingTop: 24,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  closeBtn: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
  },
  art: {
    width: ART_SIZE,
    maxWidth: '100%',
    aspectRatio: 1,
    borderRadius: 18,
    marginBottom: 14,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  lockOverlay: {
    position: 'absolute',
    width: '22%',
    aspectRatio: 1,
    borderRadius: 999,
    backgroundColor: 'rgba(6,5,18,0.9)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badge: {
    borderWidth: 2,
    borderRadius: 999,
    paddingVertical: 5,
    paddingHorizontal: 14,
    marginBottom: 10,
  },
  badgeText: {
    ...headingFont('800'),
    fontSize: 11,
    letterSpacing: em(0.04, 11),
    textTransform: 'uppercase',
  },
  name: {
    ...headingFont('800'),
    fontSize: 20,
    color: COLORS.textWarm,
    textAlign: 'center',
  },
  guild: {
    ...bodyFont('600'),
    fontSize: 12.5,
    color: COLORS.muted,
    marginTop: 6,
    textAlign: 'center',
  },
  desc: {
    ...bodyFont('400'),
    fontSize: 13,
    lineHeight: 20.8,
    color: COLORS.lilacSoft,
    marginTop: 14,
    textAlign: 'center',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 18,
  },
  status: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 7,
    paddingHorizontal: 16,
    borderRadius: 999,
    borderWidth: 1.5,
  },
  statusSolo: {
    marginTop: 18,
  },
  statusOwned: {
    backgroundColor: 'rgba(74,222,128,0.14)',
    borderColor: 'rgba(74,222,128,0.4)',
  },
  statusLocked: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderColor: 'rgba(255,255,255,0.12)',
  },
  statusText: {
    ...headingFont('800'),
    fontSize: 11.5,
  },
  statusTextOwned: {
    color: COLORS.green,
    marginLeft: 6,
  },
  sellBtn: {
    marginLeft: 8,
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: 'rgba(239,68,68,0.4)',
    backgroundColor: 'rgba(239,68,68,0.10)',
  },
  sellBtnHover: {
    backgroundColor: 'rgba(239,68,68,0.18)',
  },
  sellBtnText: {
    ...headingFont('800'),
    fontSize: 11,
    letterSpacing: em(0.02, 11),
    color: COLORS.red,
  },
  sellView: {
    width: '100%',
    alignItems: 'center',
    marginTop: 4,
  },
  sellPrompt: {
    ...bodyFont('400'),
    fontSize: 14,
    lineHeight: 22.4,
    color: COLORS.text,
    textAlign: 'center',
  },
  sellName: {
    ...bodyFont('700'),
    color: COLORS.textWarm,
  },
  sellCoin: {
    ...headingFont('800'),
    color: COLORS.gold,
  },
  sellActions: {
    flexDirection: 'row',
    width: '100%',
    marginTop: 14,
  },
  sellActionBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sellCancel: {
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  sellConfirm: {
    marginLeft: 10,
    backgroundColor: COLORS.goldDeep,
  },
  sellActionText: {
    ...headingFont('700'),
    fontSize: 13,
  },
  sellConfirmText: {
    ...headingFont('800'),
    color: '#1A1206',
  },
  coinFly: {
    height: 30,
    marginTop: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default RelicDetailModal;
